import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Trophy, CheckCircle2, TrendingDown, Zap } from "lucide-react";
import { useAppState } from "@/context/StateContext";
import { MoneyLandedModal } from "@/components/MoneyLandedModal";
import { Button } from "@/components/ui/button";
import { fmt, pct } from "@/lib/format";

function StatusBadge() {
  return (
    <span
      data-testid="status-badge-ready"
      className="inline-flex items-center gap-1.5 rounded-full bg-[#DDE7DF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#164C3B]"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-[#2F6B57]" />
      Ready
    </span>
  );
}

function TargetCard({ target }) {
  if (!target) {
    return (
      <div className="rounded-2xl border border-[#E5E0D9] bg-[#164C3B] p-6 text-white shadow-[0_8px_30px_-8px_rgba(22,76,59,0.5)]">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#C9A86A]">Current Target</p>
        <p className="mt-3 font-serif text-3xl">All clear 🎉</p>
        <p className="mt-2 text-sm text-white/70">Every eligible debt has been eliminated.</p>
      </div>
    );
  }
  const paid = target.original_balance - target.balance;
  const p = pct(paid, target.original_balance);
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#164C3B] bg-[#164C3B] p-6 text-white shadow-[0_8px_30px_-8px_rgba(22,76,59,0.5)]"
      data-testid="target-debt-card"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#C9A86A]">Current Target</p>
          <p className="mt-2 font-serif text-3xl leading-tight">{target.name}</p>
        </div>
        <span className="flex min-h-10 w-10 items-center justify-center rounded-xl bg-white/10">
          <TrendingDown className="h-5 w-5 text-[#C9A86A]" />
        </span>
      </div>
      <div className="mt-6">
        <p className="text-xs uppercase tracking-wider text-white/60">Remaining</p>
        <p className="font-serif text-5xl tracking-tight text-white" data-testid="target-remaining-amount">
          {fmt(target.balance)}
        </p>
      </div>
      <div className="mt-5">
        <div className="h-2 w-full overflow-hidden rounded-full bg-white/15">
          <div
            className="h-full rounded-full bg-[#C9A86A] transition-all duration-700"
            style={{ width: `${p}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-white/70">
          {fmt(paid)} paid down of {fmt(target.original_balance)} · {p}%
        </p>
      </div>
    </div>
  );
}

export default function Home() {
  const { state } = useAppState();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  if (!state) return null;
  const { customer, current_target, totals, debts, last_completed_sprint, eliminated_debts, active_sprint } = state;
  const activeInProgress =
    active_sprint && ["recommended", "customer_accepted", "awaiting_confirmation"].includes(active_sprint.status);

  return (
    <div className="fade-up space-y-6" data-testid="updated-dashboard-view">
      {/* Greeting */}
      <header>
        <StatusBadge />
        <h1
          className="mt-3 font-serif text-4xl leading-[1.1] text-[#171717] sm:text-5xl"
          data-testid="dashboard-greeting"
        >
          Hey, {customer?.first_name} 👋
        </h1>
        <p className="mt-2 text-lg text-[#525252]">Ready for your next move?</p>
      </header>

      {/* Current target + primary action */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <TargetCard target={current_target} />

        {activeInProgress ? (
          <div
            className="flex flex-col rounded-2xl border border-[#C9A86A] bg-white p-6"
            data-testid="sprint-in-progress-card"
          >
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#F5F2EC] px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">
              <Zap className="h-3.5 w-3.5 text-[#C9A86A]" /> Sprint in progress
            </span>
            <p className="mt-4 font-serif text-2xl leading-snug text-[#171717]">
              Your {fmt(active_sprint.potential_sprint_money)} Sprint is waiting for you.
            </p>
            <p className="mt-1.5 text-sm text-[#525252]">Pick up right where you left off.</p>
            <Button
              data-testid="continue-sprint-button"
              onClick={() => navigate("/sprint")}
              className="mt-auto min-h-12 w-full rounded-xl bg-[#2F6B57] text-base font-semibold text-white hover:bg-[#255643]"
            >
              Continue Sprint
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div
            className="flex flex-col rounded-2xl pe-surface p-6"
            data-testid="money-landed-action-card"
          >
            <p className="font-serif text-2xl text-[#171717]">Feeling rich?</p>
            <p className="mt-2 text-sm leading-relaxed text-[#525252]">
              We've already got your baseline. Tell us what came in, and we'll calculate what may be available for your next Edit.
            </p>
            <Button
              data-testid="landed-money-input-trigger"
              onClick={() => (state.foundation_ready ? setModalOpen(true) : navigate("/foundation"))}
              className="mt-5 min-h-12 w-full rounded-xl bg-[#2F6B57] text-base font-semibold text-white hover:bg-[#255643]"
            >
              Feeling rich? 💰
            </Button>
            <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-[#70584A]">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#2F6B57]" />
              We'll check your bills, spending money and safety cushion first.
            </p>
          </div>
        )}
      </div>

      {/* Supporting context */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Protection summary */}
        <div className="rounded-2xl border border-[#E5E0D9] bg-[#DDE7DF] p-6" data-testid="protection-summary-card">
          <span className="flex min-h-10 w-10 items-center justify-center rounded-xl bg-white text-[#2F6B57]">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <p className="mt-4 font-serif text-xl text-[#164C3B]">Your safety cushion</p>
          <p className="mt-1.5 text-sm leading-relaxed text-[#3f5a4c]">
            Your bills, everyday spending and safety cushion are already set aside. What's left is ready to pay down your debt.
          </p>
        </div>

        {/* Recent win / progress summary */}
        <div className="rounded-2xl pe-surface p-6" data-testid="recent-win-card">
          <span className="flex min-h-10 w-10 items-center justify-center rounded-xl bg-[#F5F2EC] text-[#C9A86A]">
            <Trophy className="h-5 w-5" />
          </span>
          {last_completed_sprint ? (
            <>
              <p className="mt-4 font-serif text-xl text-[#164C3B]">Recent win</p>
              <p className="mt-1.5 text-sm text-[#525252]">
                You put{" "}
                <span className="font-semibold text-[#C9A86A]">{fmt(last_completed_sprint.total_attacked)}</span>{" "}
                toward your debt last time.
              </p>
              {eliminated_debts.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {eliminated_debts.map((d) => (
                    <li key={d.id} className="flex items-center gap-2 text-sm text-[#164C3B]">
                      <CheckCircle2 className="h-4 w-4 text-[#2F6B57]" />
                      {d.name} paid off
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <>
              <p className="mt-4 font-serif text-xl text-[#164C3B]">Your first step</p>
              <p className="mt-1.5 text-sm text-[#525252]">
                When money comes in, tap <span className="font-medium text-[#2F6B57]">Feeling rich?</span> and we'll show you what may be available.
              </p>
            </>
          )}
        </div>

        {/* Debt progress overview */}
        <div className="rounded-2xl pe-surface p-6 md:col-span-2" data-testid="debt-progress-card">
          <div className="flex items-center justify-between">
            <p className="font-serif text-xl text-[#164C3B]">Your debts</p>
            <p className="text-sm text-[#70584A]">
              Paid toward debt{" "}
              <span className="font-semibold text-[#171717]" data-testid="total-deployed-amount">
                {fmt(totals.total_deployed)}
              </span>
            </p>
          </div>
          <div className="mt-5 space-y-4">
            {debts.map((d) => {
              const paid = d.original_balance - d.balance;
              const p = pct(paid, d.original_balance);
              return (
                <div key={d.id} data-testid={`debt-progress-${d.id}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 font-medium text-[#171717]">
                      {d.name}
                      {d.eliminated && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#DDE7DF] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#164C3B]">
                          <CheckCircle2 className="h-3 w-3" /> Paid off
                        </span>
                      )}
                    </span>
                    <span className="text-[#525252]">{fmt(d.balance)}</span>
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[#F0EBE3]">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${d.eliminated ? "bg-[#2F6B57]" : "bg-[#164C3B]"}`}
                      style={{ width: `${p}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <MoneyLandedModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
}
